import React, {useEffect, useRef} from 'react'
import {Navigate, Outlet, useLocation} from 'react-router-dom'
import {useAppSelector} from '../../store/store-hooks'
import {isEmpty} from '../../utils'
import {isTokenExpired} from '../../utils/jwt-utils'
import {rememberPostSignInPath} from '../../utils/postSignInPath'

interface PrivateRouteProps {
  children?: React.ReactNode
}

/**
 * PrivateRoute for React Router v6
 * Works with both Cognito and IOP (Single Sign) authentication flows.
 * Auth state is determined by `currentUser` (populated after sign-in) and a valid token.
 *
 * Usage:
 * - As a wrapper: <Route path="/admin" element={<PrivateRoute><AdminPage /></PrivateRoute>} />
 * - With Outlet for nested routes: <Route element={<PrivateRoute />}><Route path="..." /></Route>
 */
export default function PrivateRoute({children}: PrivateRouteProps): JSX.Element {
  const {currentUser, token} = useAppSelector((state) => state.root)
  const location = useLocation()

  const isTokenInvalid = !token || token === 'null' || isTokenExpired(token)
  const mustSignIn = isEmpty(currentUser) || isTokenInvalid

  // STFFLG-4163: a signing link opened without a session (the Review & Sign button in an eSign
  // email) is remembered here, so the sign-in pages can return the signer to that document
  // instead of the dashboard. Only /signing/ paths, and behind orderAccessInvites the two pages a
  // job order invitation email links to, are kept (see postSignInPath).
  //
  // Not the page a session ended ON. When the session this route was showing ends under it
  // (Sign out, the idle-timeout logout, a token expiring), the signout has already cleared
  // sessionStorage and this route then re-renders on that same /signing/ path: remembering it
  // there would send whoever signs in next in this tab (perhaps someone else) straight to that
  // document. A signing link opened without a session, on arrival or by moving to it once the
  // session has lapsed, is still remembered.
  const lastSessionPathRef = useRef<string | null>(null)
  useEffect(() => {
    if (!mustSignIn) {
      lastSessionPathRef.current = location.pathname
      return
    }
    if (location.pathname !== lastSessionPathRef.current) {
      rememberPostSignInPath(location.pathname, location.search)
    }
  }, [mustSignIn, location.pathname, location.search])

  if (mustSignIn) {
    return <Navigate to="/signin" replace data-testid="private-route-redirect" />
  }

  return children ? <div data-testid="private-route-content">{children}</div> : <Outlet />
}
